import type { Unsubscribe } from 'firebase/firestore';
import {
  collection,
  deleteDoc,
  doc,
  limit as firestoreLimit,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Notification, NotificationType } from '../types';

// ===== コレクション定数 =====
const NOTIFICATIONS_COLLECTION = 'notifications';

// ===== TTL設定（日数） =====
const UNREAD_TTL_DAYS = 30; // 未読通知: 30日後に削除
const READ_TTL_DAYS = 7; // 既読通知: 7日後に削除

// ===== エラーハンドリング =====
class NotificationServiceError extends Error {
  public code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'NotificationServiceError';
    this.code = code;
  }
}

// ===== ユーティリティ =====
const generateNotificationId = (): string => {
  return `notif_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// ===== 通知の作成 =====
export interface CreateNotificationData {
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  link?: string;
}

export const createNotification = async (data: CreateNotificationData): Promise<Notification> => {
  try {
    const notificationId = generateNotificationId();
    const now = new Date().toISOString();

    const notification: Notification = {
      id: notificationId,
      userId: data.userId,
      type: data.type,
      title: data.title,
      body: data.body,
      link: data.link,
      read: false,
      createdAt: now,
    };

    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await setDoc(notificationRef, notification);

    return notification;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw new NotificationServiceError('通知の作成に失敗しました');
  }
};

// ===== メンション通知の作成 =====
export interface MentionNotificationData {
  mentionedUserId: string;
  mentionerName: string;
  workspaceName: string;
  workspaceId: string;
  messagePreview: string;
}

export const createMentionNotification = async (
  data: MentionNotificationData,
): Promise<Notification> => {
  return createNotification({
    userId: data.mentionedUserId,
    type: 'mention',
    title: `${data.mentionerName}があなたをメンションしました`,
    body: `${data.workspaceName}: "${data.messagePreview.slice(0, 50)}${data.messagePreview.length > 50 ? '...' : ''}"`,
    link: `/share/${data.workspaceId}`,
  });
};

// ===== アイテム完了通知の作成 =====
export interface ItemCompletedNotificationData {
  userId: string;
  itemTitle: string;
  itemId: string;
  workspaceId?: string;
}

export const createItemCompletedNotification = async (
  data: ItemCompletedNotificationData,
): Promise<Notification> => {
  return createNotification({
    userId: data.userId,
    type: 'item_completed',
    title: 'ファクトチェックが完了しました',
    body: `「${data.itemTitle}」の検証が完了しました`,
    link: data.workspaceId
      ? `/share/${data.workspaceId}`
      : `/results/${encodeURIComponent(data.itemId)}`,
  });
};

// ===== ユーザーの通知を取得 =====
export const getUserNotifications = async (
  userId: string,
  notificationLimit: number = 50,
): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(notificationLimit),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as Notification);
  } catch (error) {
    console.error('Error getting user notifications:', error);
    throw new NotificationServiceError('通知の取得に失敗しました');
  }
};

// ===== 未読通知数を取得 =====
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(notificationsRef, where('userId', '==', userId), where('read', '==', false));

    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

// ===== 通知を既読にする =====
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await updateDoc(notificationRef, { read: true });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw new NotificationServiceError('通知の既読処理に失敗しました');
  }
};

// ===== 全ての通知を既読にする =====
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(notificationsRef, where('userId', '==', userId), where('read', '==', false));

    const snapshot = await getDocs(q);

    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw new NotificationServiceError('通知の一括既読処理に失敗しました');
  }
};

// ===== 通知を削除 =====
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw new NotificationServiceError('通知の削除に失敗しました');
  }
};

// ===== 全ての通知を削除 =====
export const deleteAllNotifications = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(notificationsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnapshot) => {
      batch.delete(docSnapshot.ref);
    });

    await batch.commit();
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw new NotificationServiceError('通知の一括削除に失敗しました');
  }
};

// ===== 期限切れ通知のクリーンアップ =====
export const cleanupExpiredNotifications = async (userId: string): Promise<number> => {
  try {
    const now = new Date();
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(notificationsRef, where('userId', '==', userId));

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    let deletedCount = 0;

    snapshot.docs.forEach((docSnapshot) => {
      const notification = docSnapshot.data() as Notification;
      const createdAt = new Date(notification.createdAt);
      const daysDiff = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

      // 既読: 7日以上、未読: 30日以上で削除
      const shouldDelete = notification.read
        ? daysDiff >= READ_TTL_DAYS
        : daysDiff >= UNREAD_TTL_DAYS;

      if (shouldDelete) {
        batch.delete(docSnapshot.ref);
        deletedCount++;
      }
    });

    if (deletedCount > 0) {
      await batch.commit();
    }

    return deletedCount;
  } catch (error) {
    console.error('Error cleaning up expired notifications:', error);
    return 0; // エラー時は0を返す（クリーンアップ失敗は致命的ではない）
  }
};

// ===== 通知をリアルタイム監視 =====
export const subscribeToNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void,
  notificationLimit: number = 50,
): Unsubscribe => {
  const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
  const q = query(
    notificationsRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    firestoreLimit(notificationLimit),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map((doc) => doc.data() as Notification);
      callback(notifications);
    },
    (error) => {
      console.error('Error subscribing to notifications:', error);
    },
  );
};

// ===== 未読数をリアルタイム監視 =====
export const subscribeToUnreadCount = (
  userId: string,
  callback: (count: number) => void,
): Unsubscribe => {
  const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
  const q = query(notificationsRef, where('userId', '==', userId), where('read', '==', false));

  return onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.size);
    },
    (error) => {
      console.error('Error subscribing to unread count:', error);
      callback(0);
    },
  );
};
